import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { uploadQuote } from "../api/api";
import { trackEvent, trackException } from "../telemetry";
import { useDxfInsunitsValidation } from "../hooks";
import DxfInsunitsWarning from "./DxfInsunitsWarning";

const FileUpload = ({
  accept = ".dxf",
  label = "Browse Files",
  instructions = "We accept DXF files for instant quotes",
  onFileDrop,
  className,
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]); // Track uploaded files

  // Check if filename contains special characters
  const hasSpecialCharacters = (fileName) => {
    // Check if filename contains any character that's not alphanumeric, dot, hyphen, or underscore
    return /[^a-zA-Z0-9._-]/.test(fileName);
  };

  // Sanitize filename by removing special characters
  const sanitizeFileName = (fileName) => {
    // Get file extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;

    // Remove special characters, keep only alphanumeric, dots, hyphens, and underscores
    const sanitized = nameWithoutExt.replace(/[^a-zA-Z0-9._-]/g, '');

    // Ensure filename is not empty after sanitization
    const finalName = sanitized || 'file';

    return finalName + extension;
  };

  const doUpload = useCallback(
    async (filesToUpload) => {
      if (!onFileDrop) return;
      setUploadedFiles((prev) => [...prev, ...filesToUpload]);
      const formData = new FormData();
      for (let i = 0; i < filesToUpload.length; i++) {
        const originalFile = filesToUpload[i];
        if (hasSpecialCharacters(originalFile.name)) {
          const sanitizedName = sanitizeFileName(originalFile.name);

          // Create a new File object with sanitized name
          const sanitizedFile = new File([originalFile], sanitizedName, {
            type: originalFile.type,
            lastModified: originalFile.lastModified,
          });

          formData.append("quote_image", sanitizedFile);
        } else {
          // Use original file if no special characters
          formData.append("quote_image", originalFile);
        }
      }

      formData.append("documentName", "file_upload");
      formData.append("workspaceName", "file_upload");

      const quote_list = localStorage.getItem("setItemelementData");

      if (quote_list) {
        const quote_list_val = JSON.parse(quote_list);
        formData.append("id", quote_list_val._id);
        formData.append("type", 0);
      }

      setProcessing(true);
      const uploadStartTime = Date.now();

      try {
        const response = await uploadQuote(formData);
        const processingTime = Date.now() - uploadStartTime;
        trackEvent("DXF_File_Upload", {
          fileCount: filesToUpload.length,
          totalSizeKB: Math.round(
            filesToUpload.reduce((sum, f) => sum + f.size, 0) / 1024
          ),
          processingTimeMs: processingTime,
          partsCreated: response.data.partsDBdata?.length || 0,
          success: true,
          timestamp: new Date().toISOString(),
        });
        localStorage.setItem(
          "setItemelementData",
          JSON.stringify(response.data.requestQuoteDB)
        );

        localStorage.setItem(
          "setItempartsDBdata",
          JSON.stringify([
            ...(JSON.parse(localStorage.getItem("setItempartsDBdata")) || []),
            ...response.data.partsDBdata,
          ])
        );

        onFileDrop({
          requestQuoteDB: response.data.requestQuoteDB,
          partsDBdata: response.data.partsDBdata,
        });

        setProcessing(false);
        navigate("/quotes/quotes-detail");
      } catch (err) {
        const processingTime = Date.now() - uploadStartTime;
        trackEvent("DXF_File_Upload", {
          fileCount: filesToUpload.length,
          processingTimeMs: processingTime,
          success: false,
          errorMessage: err.response?.data?.message || err.message,
          timestamp: new Date().toISOString(),
        });
        trackException(err, { context: "DXF_File_Upload" });
        toast.error(err.response?.data?.message || "Bad Request");
        setProcessing(false);
      }
    },
    [onFileDrop, navigate]
  );

  const {
    invalidFileNames,
    dimensionTypeByFile,
    setDimensionForFile,
    validateOnDrop,
    handleFixFiles,
    resetInsunits,
  } = useDxfInsunitsValidation(doUpload);

  const onDrop = async (acceptedFiles, fileRejections) => {
    setError("");
    resetInsunits();

    // Guest upload limits
    var count = JSON.parse(localStorage.getItem("setItempartsDBdata"));
    const token = localStorage.getItem("authToken");
    if (!token && uploadedFiles.length + acceptedFiles.length > 5) {
      setError(
        "You can only upload a maximum of 5 files. Please login to upload more dxf file."
      );
      return;
    }
    if (count != null) {
      if (!token && count.length > 5) {
        setError(
          "You can only upload a maximum of 5 files. Please login to upload more dxf file."
        );
        return;
      }
    }
    if (uploadedFiles.length + acceptedFiles.length > 150) {
      setError("You can only upload a maximum of 150 files.");
      return;
    }

    if (fileRejections.length > 0) {
      setError(`Only ${accept.toUpperCase()} files are accepted.`);
      return;
    }

    const isValid = await validateOnDrop(acceptedFiles);
    if (!isValid) return;

    if (onFileDrop) {
      await doUpload(acceptedFiles);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "application/octet-stream": [accept], // Adjust MIME type as needed
    },
    onDrop,
  });

  return (
    <div>
      <div
        className={`banner-upload-file mb-2 text-center ${className} ${processing ? "processing-added" : ""
          }`}
        {...getRootProps()}
      >
        {processing && (
          <>
            <div className="loader-overlay"></div>
            <div className="loader">
              <Icon icon="tabler:loader-2" />
            </div>
          </>
        )}
        <Icon icon="mage:file-plus" />
        <p>
          <label>{label}</label>
          <input
            {...getInputProps()}
            type="file"
            id="uploadfile"
            name="uploadfile"
            className="d-none"
          />
          <span> or Drag or Drop</span>
        </p>
        <small>{instructions}</small>
      </div>
      {error && invalidFileNames.length === 0 && (
        <div className="text-danger mb-3">{error}</div>
      )}
      <DxfInsunitsWarning
        invalidFileNames={invalidFileNames}
        dimensionTypeByFile={dimensionTypeByFile}
        onDimensionSelect={setDimensionForFile}
        onFixFiles={handleFixFiles}
        processing={processing}
      />
    </div>
  );
};

export default FileUpload;
